"use client";
import GlobalForm from "@/components/forms/GlobalForm";
import useModalScrollLock from "@/hooks/useModalScrollLock";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";
import Image from "next/image";
import { usePathname } from "next/navigation";
import React, { useState, useEffect, useRef } from "react";

interface PanIndiaPresenceProps {
  pageName: string;
  title: React.ReactNode;
}

const Pan_India_presence: React.FC<PanIndiaPresenceProps> = ({
  pageName,
  title,
}) => {
  const pathname = usePathname();

  // Hide button on specific URLs
  const shouldHideButton =
    pathname === "/best-us-cpa-admission-online-course-fees-in-india" ||
    pathname === "/best-us-cma-coaching-in-india";

  const images = [
    "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/office1.png?tr=f-webp",
    "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/office2.png?tr=f-webp",
    "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/office3.png?tr=f-webp",
    "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/office4.png?tr=f-webp",
    "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/office5.png?tr=f-webp",
    "https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/office6.png?tr=f-webp",
  ];

  const locations = [
    { name: "Bangalore", href: "/cpa/best-cpa-course-in-bangalore" },
    { name: "Hyderabad", href: "/contact-us" },
    { name: "Mumbai", href: "/contact-us" },
    { name: "Delhi", href: "/contact-us" },
    { name: "Pune", href: "/contact-us" },
    { name: "Ahmedabad", href: "/contact-us" },
    { name: "Ernakulam", href: "/contact-us" },
    { name: "Kolkata", href: "/contact-us" },
    { name: "Chennai", href: "/contact-us" },
  ];

  const [activeImage, setActiveImage] = useState(images[0]);
  const [fade, setFade] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState<boolean>(false);
  const [isCityFormModalOpen, setIsCityFormModalOpen] =
    useState<boolean>(false);
  const [modalDefaultCity, setModalDefaultCity] = useState<string | null>(null);

  const handleCityFormModalOpen = (cityName: string) => {
    sessionStorage.setItem("modal-open", "true");
    setModalDefaultCity(cityName);
    setIsCityFormModalOpen(true);
  };

  const handleCityFormModalClose = () => {
    sessionStorage.removeItem("modal-open");
    setModalDefaultCity(null);
    setIsCityFormModalOpen(false);
  };

  const handleFormModelOpen = () => {
    sessionStorage.setItem("modal-open", "true");
    setIsFormModalOpen(true);
  };

  const handleFormModelClose = () => {
    sessionStorage.removeItem("modal-open");
    setIsFormModalOpen(false);
  };

  useModalScrollLock(isFormModalOpen, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  useEffect(() => {
    if (!isHovered) {
      intervalRef.current = setInterval(() => {
        setFade(true);
        setTimeout(() => {
          const currentIndex = images.indexOf(activeImage);
          const nextIndex = (currentIndex + 1) % images.length;
          setActiveImage(images[nextIndex]);
          setFade(false);
        }, 300);
      }, 3000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [activeImage, isHovered]);

  const handleImageSelect = (img: string) => {
    if (img === activeImage) return;
    setFade(true);
    setTimeout(() => {
      setActiveImage(img);
      setFade(false);
    }, 300);
  };

  return (
    <>
      <div className="w-full bg-white relative">
        <Image
          src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa/panIndia.png?tr=f-webp"
          alt="Miles Logo Background"
          fill
          className="object-cover z-0"
          loading="lazy"
          quality={75}
          sizes="100vw"
        />
        <div className="main_container mx-auto py-8 lg:py-16 relative z-10">
          <div className="flex flex-col-reverse lg:flex-row gap-4 xl:gap-8">
            {/* Mobile CTA - Hidden on /best-us-cpa-admission-online-course-fees-in-india */}
            {!shouldHideButton && (
              <div className="lg:hidden text-center">
                <button
                  onClick={handleFormModelOpen}
                  className="bg-[#39F] rounded-tl-[13.333px] rounded-tr-[13.333px] rounded-br-[13.333px] rounded-bl-[0px] text-white px-4 py-2 font-semibold"
                >
                  Visit your nearest Office
                </button>
              </div>
            )}
            <div
              className="relative flex justify-center w-full"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div className="relative w-full">
                <Image
                  src={`${activeImage}&tr=w-385,h-255,q-60`}
                  alt="Pan India Office Preview"
                  width={385}
                  height={255}
                  className={`w-full max-h-[350px] rounded-[20px] transition-opacity duration-300 ease-in-out object-cover ${
                    fade ? "opacity-0" : "opacity-100"
                  }`}
                  loading="lazy"
                  sizes="(max-width: 767px) 380px, 600px"
                />
              </div>

              <div className="flex gap-2 mt-4 justify-center flex-wrap absolute bottom-1 lg:bottom-5 rounded-[10px] backdrop-blur-sm bg-black/10 p-2">
                {images?.map((img, index) => (
                  <div
                    key={index}
                    onClick={() => handleImageSelect(img)}
                    className={`relative w-[45px] lg:w-[60px] h-[45px] lg:h-[60px] border-2 rounded-[10px] overflow-hidden cursor-pointer transition-all duration-300 hover:border-[#39F] hover:scale-105 ${
                      activeImage === img
                        ? "border-[#39F] shadow-lg"
                        : "border-gray-300"
                    }`}
                  >
                    <Image
                      src={`${img}&tr=w-70,h-70,q-60`}
                      alt={`thumbnail-${index}`}
                      width={70}
                      height={70}
                      className="object-cover w-full h-full"
                      loading="lazy"
                      sizes="70px"
                    />
                    {activeImage === img && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-[10px]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="white"
                          className="w-5 h-5 drop-shadow-lg"
                        >
                          <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
                          <path
                            fillRule="evenodd"
                            d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 010-1.113zM17.25 12a5.25 5.25 0 11-10.5 0 5.25 5.25 0 0110.5 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2 lg:gap-5 justify-start lg:justify-center w-full">
              <div className="text-[20px] lg:text-[36px] font-semibold text-black text-center leading-[1.2]">
                {title}
              </div>
              <p className="text-center text-sm md:text-[18px] max-w-[500px] mx-auto leading-[1]">
                Visit the nearest Miles office or Schedule Online Meet
              </p>
              <div className="flex flex-wrap gap-2 lg:gap-4 justify-center mt-4 md:mt-0">
                {locations?.map((location, index) => (
                  <button
                    key={index}
                    onClick={() => handleCityFormModalOpen(location.name)}
                    className="bg-white text-[#39f] font-semibold px-4 py-2 rounded-[12px] border-[0.75px] border-[#C9C9C9] hover:bg-[#39F] hover:text-white transition-colors duration-300"
                  >
                    {location.name}
                  </button>
                ))}
              </div>
              {/* Desktop CTA - Hidden on /best-us-cpa-admission-online-course-fees-in-india */}
              {!shouldHideButton && (
                <div className="hidden lg:block text-center">
                  <button
                    onClick={handleFormModelOpen}
                    className="bg-[#39F] rounded-tl-[13.333px] rounded-tr-[13.333px] rounded-br-[13.333px] rounded-bl-[0px] text-white px-4 py-2 font-semibold"
                  >
                    Visit your nearest Office
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isCityFormModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-[auto] mx-auto border border-white p-4 w-[400px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={handleCityFormModalClose}
              className="text-white text-2xl absolute right-0 top-1 cursor-pointer"
            >
              ✕
            </button>
            <div>
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp"
                alt="star group"
                width={34}
                height={38}
                className="absolute top-3 left-[-8px]"
                loading="lazy"
              />
              <GlobalForm
                course={pageName === "cpa" ? "CPA" : "CMA"}
                courseId={pageName === "cpa" ? 1 : 2}
                formHeading={
                  FormHeading.SCHEDULE_A_VISIT_TO_MILES_OFFICE_OR_MEET_US_ONLINE
                }
                formType="page"
                isAStepperForm={true}
                pageType="accounting page"
                subCategory={
                  pageName === "cpa"
                    ? FinanceAndAccountingSubcategory.CPA
                    : FinanceAndAccountingSubcategory.CMA
                }
                submitButtonName="Start Your Journey"
                successMessage={SuccessMessageType.ACCOUNTING}
                verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                darkBackground={true}
                defaultCityLocation={modalDefaultCity ?? undefined}
              />
            </div>
          </div>
        </div>
      )}

      {isFormModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-[999] lg:p-10 p-4">
          <div
            className="rounded-2xl shadow-lg relative h-[auto] mx-auto border border-white p-4 w-[400px]"
            style={{
              background:
                "linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(51, 153, 255, 0.30) 100%)",
              boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)",
              backdropFilter: "blur(28px)",
            }}
          >
            <button
              onClick={handleFormModelClose}
              className="text-white text-2xl absolute right-0 top-1 cursor-pointer"
            >
              ✕
            </button>
            <div>
              <Image
                src="https://ik.imagekit.io/mileseducation/miles_website/accounting/cpa-visit/starGroup.webp"
                alt="star group"
                width={34}
                height={38}
                className="absolute top-3 left-[-8px]"
                loading="lazy"
              />
              <GlobalForm
                course={pageName === "cpa" ? "CPA" : "CMA"}
                courseId={pageName === "cpa" ? 1 : 2}
                formHeading={
                  FormHeading.SCHEDULE_A_VISIT_TO_MILES_OFFICE_OR_MEET_US_ONLINE
                }
                formType="page"
                isAStepperForm={true}
                pageType="accounting page"
                subCategory={
                  pageName === "cpa"
                    ? FinanceAndAccountingSubcategory.CPA
                    : FinanceAndAccountingSubcategory.CMA
                }
                submitButtonName="Start Your Journey"
                successMessage={SuccessMessageType.ACCOUNTING}
                verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
                darkBackground={true}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Pan_India_presence;
